/**
 * Task schemas — assessment assignment, form requests, referrals.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type {
  TaskStatus as FhirTaskStatus,
  TaskTypeCode as FhirTaskTypeCode,
} from '@unify/fhir-model/resources/task';

/** FHIR Task.status values (Unify subset). */
export const taskStatusValues = [
  'draft',
  'requested',
  'received',
  'accepted',
  'rejected',
  'in-progress',
  'completed',
  'cancelled',
] as const satisfies readonly FhirTaskStatus[];

/** Unify task type codes — synced with fhir-model. */
export const taskTypeValues = [
  'form',
  'send-email-notification',
  'send-sms-notification',
] as const satisfies readonly FhirTaskTypeCode[];

/**
 * Assessment assignment Task creation.
 */
export const assessmentAssignmentSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  questionnaireId: z.string().min(1, 'Assessment questionnaire is required'),
  requesterId: z.string().optional(),
  requesterDisplay: z.string().optional(),
  episodeOfCareId: z.string().optional(),
  accountId: z.string().optional(),
  dueDate: z.string().optional(),
  recurrencePattern: z.string().optional(),
});

export type AssessmentAssignmentInput = z.infer<typeof assessmentAssignmentSchema>;

/**
 * Form request Task.
 */
export const formRequestSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  questionnaireId: z.string().min(1, 'Form template is required'),
  requesterId: z.string().optional(),
  requesterDisplay: z.string().optional(),
  episodeOfCareId: z.string().optional(),
  dueDate: z.string().optional(),
  description: z.string().optional(),
});

export type FormRequestInput = z.infer<typeof formRequestSchema>;

/**
 * Referral request Task.
 */
export const referralRequestSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  requesterId: z.string().min(1, 'Requester is required'),
  ownerId: z.string().optional(),
  therapyTypeCode: z.string().optional(),
  notes: z.string().optional(),
  episodeOfCareId: z.string().optional(),
});

export type ReferralRequestInput = z.infer<typeof referralRequestSchema>;
